import CheckCircle from "@mui/icons-material/CheckCircle";
import { Alert, Box, Button, Paper, Stack, Typography } from "@mui/material";
import { useAlerts } from "components/Alerts/AlertProvider";
import { ConfirmDialog } from "components/ConfirmDialog";
import { Reviewer, ReviewerPicker } from "components/Submission/ReviewersEditList";
import { getFullName } from "encamp-shared/src/utils/name";
import {
  ReviewerStatus,
  SubmissionStatus,
  SubmissionStepStatus,
  SubmissionStepType,
} from "generated-graphql/graphql";
import { useCurrentUser } from "hooks/useCurrentUser";
import {
  useSetSubmissionStepAssignee,
  useUpdateSubmissionStatus,
  useVerifyAndSubmitSubmission,
} from "hooks/useSubmissionMutations";
import { SubmissionStepState } from "hooks/useSubmissionSteps";
import { useTouchSubmissionMutation } from "hooks/useTouchSubmissionMutation";
import { useWasteStepper } from "hooks/useWasteStepper";
import { useCallback, useEffect, useMemo, useState } from "react";

import { StepHeader } from "../StepHeader";

type StepAssignee = NonNullable<
  NonNullable<ReturnType<typeof useWasteStepper>["submission"]>["stepperSteps"][number]["assignee"]
>;

/** "Name (email)" when we have a person record, otherwise just the email. */
function assigneeLabel(assignee: StepAssignee): string {
  const { person, email } = assignee;
  return person ? `${getFullName(person)} (${email})` : email;
}

/** Map the persisted step assignee to the user-picker's option shape. */
function toPickerValue(assignee: StepAssignee | null | undefined): Reviewer | null {
  if (!assignee) {
    return null;
  }
  return {
    id: assignee.id,
    name: assignee.person ? getFullName(assignee.person) : assignee.email,
    email: assignee.email,
    order: 0,
    isStaff: false,
  };
}

/**
 * Verify & Submit step — the terminal action. A single "Verify & Submit"
 * button stays greyed out until every reviewer has approved; clicking it sends
 * the submission to PROCESSING_SUBMISSION (freezing the snapshot) and marks this
 * step done. It shows a compact approval summary, but reviewers approve/reject on
 * the Review step (not here). Once frozen (processing/filed), this just notes the
 * status and offers staff the "mark as filing complete" action.
 *
 * Verifying is gated to staff or the step's assigned verifier: staff designate a
 * verifier (a tenant user, picked from the same tenant-scoped `users` query as
 * reviewers) who then sees the Verify & Submit button even though the rest of
 * the stepper stays staff-edited (`canEdit = isStaff`).
 */
export function VerifyAndSubmitStep({ submissionId }: { submissionId: string }) {
  const alerts = useAlerts();
  const { isStaff, user } = useCurrentUser();
  const { submission, canEdit, isFrozen, stepStates } = useWasteStepper();
  const [verifyAndSubmit, { loading: submitting }] = useVerifyAndSubmitSubmission();
  const [updateSubmissionStatus, { loading: markingComplete }] = useUpdateSubmissionStatus();
  const [setStepAssignee, { loading: savingVerifier }] = useSetSubmissionStepAssignee();
  const { touchStep } = useTouchSubmissionMutation(submissionId);
  const [markCompleteOpen, setMarkCompleteOpen] = useState(false);

  const status = submission?.status ?? SubmissionStatus.InProgress;

  // The designated verifier — the assignee on this submission's
  // VERIFY_AND_SUBMIT step row.
  const verifier = useMemo(
    () =>
      submission?.stepperSteps?.find((s) => s.step === SubmissionStepType.VerifyAndSubmit)
        ?.assignee ?? null,
    [submission]
  );
  const isVerifier = !!user?.id && user.id === verifier?.id;
  // Staff always retain the verify override; the verifier may verify until the
  // submission freezes.
  const canVerify = canEdit || (isVerifier && !isFrozen);

  // Draft verifier selection — deliberately NOT autosaved on pick; the staff
  // user confirms with the Save button below, which is when the assignment
  // (and the verify permission it grants) actually changes hands.
  const [draftVerifier, setDraftVerifier] = useState<Reviewer | null>(() =>
    toPickerValue(verifier)
  );
  // Re-sync the draft whenever a refetch lands so the picker tracks the
  // persisted assignee (e.g. after save, or another staff user's change).
  useEffect(() => {
    setDraftVerifier(toPickerValue(verifier));
  }, [verifier]);
  const verifierDirty = (draftVerifier?.id ?? null) !== (verifier?.id ?? null);
  const reviewers = useMemo(
    () => (submission?.reviewers ?? []).filter((r) => !r.isSigner),
    [submission]
  );
  const approvedCount = reviewers.filter(
    (r) => r.reviewerStatus === ReviewerStatus.Approved
  ).length;

  // Every assigned reviewer has approved — staff can now verify & submit.
  const allReviewersApproved = reviewers.length > 0 && approvedCount === reviewers.length;

  // The no-review path: staff explicitly confirmed "No review required" on the
  // Review step and advanced through it (stepper creation persists
  // reviewRequired=true, so false is always a deliberate opt-out). The
  // completed-step check keeps pre-default-flip submissions from skipping
  // review just because they were created with reviewRequired=false.
  const reviewNotRequired =
    submission?.reviewRequired === false &&
    reviewers.length === 0 &&
    stepStates[SubmissionStepType.Review] === SubmissionStepState.Done;

  const readyToSubmit = allReviewersApproved || reviewNotRequired;

  const handleSaveVerifier = useCallback(async () => {
    try {
      await setStepAssignee({
        variables: {
          submissionId,
          step: SubmissionStepType.VerifyAndSubmit,
          assigneeUserId: draftVerifier?.id ?? null,
        },
      });
      alerts.success(draftVerifier ? "Verifier assigned." : "Verifier removed.");
    } catch (error) {
      alerts.error("Failed to update the verifier.", error);
    }
  }, [setStepAssignee, submissionId, draftVerifier, alerts]);

  const handleVerifyAndSubmit = useCallback(async () => {
    try {
      const result = await verifyAndSubmit({ variables: { submissionId } });
      if (result.data?.verifyAndSubmitSubmission) {
        // Reaching PROCESSING_SUBMISSION is the terminal action of this step.
        await touchStep(SubmissionStepType.VerifyAndSubmit, SubmissionStepStatus.Completed);
        alerts.success("Submitted for processing.");
      }
    } catch (error) {
      alerts.error("Failed to submit for processing.", error);
    }
  }, [verifyAndSubmit, touchStep, submissionId, alerts]);

  const handleMarkFilingComplete = useCallback(async () => {
    try {
      await updateSubmissionStatus({
        variables: {
          input: {
            submissionId,
            submissionStatus: SubmissionStatus.FilingComplete,
          },
        },
      });
      alerts.success("Marked as filing complete.");
      setMarkCompleteOpen(false);
    } catch (error) {
      alerts.error("Failed to mark as filing complete.", error);
    }
  }, [updateSubmissionStatus, submissionId, alerts]);

  const isFrozenStatus =
    status === SubmissionStatus.ProcessingSubmission || status === SubmissionStatus.FilingComplete;

  // Compact, read-only approval summary; the full reviewer list + approve/reject
  // action live on the Review step.
  const renderReviewerSummary = () => {
    if (reviewNotRequired) {
      return <Alert severity="info">No review was required for this submission.</Alert>;
    }
    if (reviewers.length === 0) {
      return (
        <Alert severity="info">
          Add reviewers and send this submission for review from the Review step first.
        </Alert>
      );
    }
    if (allReviewersApproved) {
      return <Alert severity="success">All reviewers have approved.</Alert>;
    }
    const pending = reviewers.length - approvedCount;
    return (
      <Alert severity="info">
        Waiting on {pending} of {reviewers.length} reviewer{reviewers.length === 1 ? "" : "s"} to
        approve — reviews happen on the Review step.
      </Alert>
    );
  };

  return (
    <Stack spacing={2}>
      <StepHeader title="Verify & Submit" />

      {isFrozenStatus ? (
        <Stack spacing={2}>
          <Alert severity="info">
            {status === SubmissionStatus.FilingComplete
              ? "This submission has been filed."
              : "Submitted for processing — it will be filed automatically. See the left-nav for status."}
          </Alert>
          {/*
           * Staff escape hatch: until the RCRAInfo filing automation ships, a
           * submission can sit in PROCESSING_SUBMISSION indefinitely, so staff
           * still need to mark it filed manually. This is the normal post-approval
           * completion, so it only renders once every reviewer has approved (or
           * the no-review opt-out was explicitly confirmed on the Review step). The
           * old "Force Complete" bypass for not-yet-approved submissions has been
           * deprecated and removed; completion now always requires full approval.
           * Gated on `isStaff` (canEdit is false once frozen);
           * useUpdateSubmissionStatus refetches GetSubmissionStepper, flipping
           * this step to the filed state.
           */}
          {isStaff && status === SubmissionStatus.ProcessingSubmission && readyToSubmit && (
            <>
              <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
                <Button
                  variant="contained"
                  color="success"
                  startIcon={<CheckCircle />}
                  onClick={() => setMarkCompleteOpen(true)}
                >
                  Mark as filing complete
                </Button>
              </Box>
              <ConfirmDialog
                open={markCompleteOpen}
                loading={markingComplete}
                onClose={() => setMarkCompleteOpen(false)}
                onConfirm={handleMarkFilingComplete}
                header="Confirm Status Update"
                info="Are you sure you want to mark this submission as Filing Complete? This action cannot be undone."
                confirmText="Mark as Complete"
                cancelText="Cancel"
              />
            </>
          )}
        </Stack>
      ) : (
        <>
          <Typography variant="body2" color="text.secondary">
            {reviewNotRequired
              ? "Verify & submit to send this 8700-12 notification for processing."
              : "Once every reviewer has approved, verify & submit to send this 8700-12 notification for processing."}
          </Typography>

          {/* The verifier is the customer user allowed to click Verify &
              Submit (staff always retain the override). Staff assign them
              here; everyone else sees who is on the hook. The picker reuses
              the reviewer picker's tenant-scoped `users` query. */}
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Stack spacing={1}>
              <Typography variant="subtitle1" fontWeight="medium">
                Verifier
              </Typography>
              <Typography variant="body2" color="text.secondary">
                The verifier reviews the final 8700-12 notification and submits it for processing.
              </Typography>
              {canEdit ? (
                <>
                  <ReviewerPicker
                    value={draftVerifier}
                    onChange={setDraftVerifier}
                    disabled={savingVerifier}
                    inputLabelProps={{ label: "Verifier", size: "small" }}
                    sx={{ maxWidth: 480 }}
                  />
                  <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
                    <Button
                      variant="outlined"
                      disabled={!verifierDirty}
                      loading={savingVerifier}
                      onClick={handleSaveVerifier}
                    >
                      Save Verifier
                    </Button>
                  </Box>
                </>
              ) : (
                <Typography variant="body2">
                  {verifier ? assigneeLabel(verifier) : "No verifier assigned."}
                </Typography>
              )}
            </Stack>
          </Paper>

          {renderReviewerSummary()}

          {canVerify && (
            <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
              <Button
                variant="contained"
                disabled={!readyToSubmit}
                loading={submitting}
                onClick={handleVerifyAndSubmit}
              >
                Verify &amp; Submit
              </Button>
            </Box>
          )}
        </>
      )}
    </Stack>
  );
}
